<script setup lang="ts">
  import {
    Button,
    Command,
    CommandGroup,
    CommandItem,
    CommandList,
    Kbd,
    ModalSectionHeader,
    ModalSectionHeaderTitle
  } from '@gat-ship/vue-components'
  // Sibling step components share an identical interface and listbox wiring by
  // design (uniform `:source`/`select` contract); only the row markup varies.
  // fallow-ignore-next-line code-duplication
  import { IconCheck, IconPlus, IconStakeholder } from '@gat-ship/icons'
  import type { StepOption } from './create-portcall-steps'
  import type { StepListSource } from '../../../../composables/usePortcallStepSources'
  import { usePortcallStepListbox } from '../../../../composables/usePortcallStepListbox'
  import { useCreatePortcallPermissions } from '../../../../composables/useCreatePortcallPermissions'
  import PortcallStepSearch from './PortcallStepSearch.vue'
  import PortcallStepLoading from './PortcallStepLoading.vue'
  import PortcallStepEmpty from './PortcallStepEmpty.vue'

  /**
   * The appointing-principal step (Who). Server search — no rows until the
   * user types — so the empty state shows a prompt first. The stakeholder icon
   * keeps its own colour (no muted class).
   */
  const props = defineProps<{
    source: StepListSource
    selectedValue?: string
    /** Mobile keyboard is up — collapse the question block to free list room. */
    keyboardOpen?: boolean
  }>()
  const emit = defineEmits<{
    select: [option: StepOption, advance: boolean]
    'update:query': [value: string]
    addClient: []
  }>()

  const {
    onKeyboardAdvance,
    onPointerSelect,
    onSelect,
    selectImmediate,
    awaitingFirstSearch
  } = usePortcallStepListbox(
    props.source,
    (option, advance) => emit('select', option, advance),
    { searchFirst: true, suppressEnterSelect: () => !!props.keyboardOpen }
  )

  const { canAddClient } = useCreatePortcallPermissions()
</script>

<template>
  <Command
    ref="commandRef"
    class="flex h-auto min-h-0 flex-1 flex-col overflow-hidden bg-transparent"
    @keydown.enter.capture="onKeyboardAdvance"
  >
    <div :class="['flex-none px-6 pb-3', keyboardOpen ? 'pt-4' : 'pt-6']">
      <ModalSectionHeader v-if="!keyboardOpen" class="gap-0">
        <div
          class="text-muted-foreground mb-2 text-[11px] font-semibold uppercase tracking-wider"
        >
          Principal
        </div>
        <ModalSectionHeaderTitle class="text-lg font-semibold leading-tight">
          Who is nominating you?
        </ModalSectionHeaderTitle>
      </ModalSectionHeader>
      <PortcallStepSearch
        class="mt-4"
        :model-value="source.query.value"
        :keyboard-open="keyboardOpen"
        placeholder="Search clients…"
        @update:model-value="emit('update:query', $event)"
      />
    </div>

    <CommandList class="max-h-none min-h-0 flex-1 px-6 pb-4">
      <PortcallStepLoading v-if="source.loading.value" />

      <CommandGroup
        v-else-if="source.options.value.length"
        class="divide-border divide-y p-0"
      >
        <CommandItem
          v-for="opt in source.options.value"
          :key="opt.value"
          :value="opt.value"
          class="group gap-3 rounded-none px-2 py-3"
          @select="onSelect(opt)"
          @dblclick="selectImmediate(opt)"
          @pointerdown="onPointerSelect"
        >
          <IconStakeholder stakeholder-type="client" class="size-4 shrink-0" />
          <div class="min-w-0 flex-1">
            <div
              class="truncate text-sm font-medium"
              :class="selectedValue === opt.value && 'text-primary'"
            >
              {{ opt.label }}
            </div>
            <div v-if="opt.sub" class="text-muted-foreground truncate text-xs">
              {{ opt.sub }}
            </div>
          </div>
          <div class="ml-auto flex items-center gap-2">
            <Kbd class="hidden md:group-data-[highlighted]:inline-flex">↵</Kbd>
            <IconCheck
              v-if="selectedValue === opt.value"
              class="text-primary size-4 shrink-0"
            />
          </div>
        </CommandItem>
      </CommandGroup>

      <PortcallStepEmpty
        v-else
        :awaiting-first-search="awaitingFirstSearch"
        title="No clients found"
        prompt="Start typing to search principals…"
        empty-text="Try a different search term or add a new client"
      >
        <Button
          v-if="canAddClient"
          variant="outline"
          @click="emit('addClient')"
        >
          <IconPlus class="size-4" />
          Add client
        </Button>
      </PortcallStepEmpty>
    </CommandList>
  </Command>
</template>
